import * as mutationTypes from '@/store/mutation-types'
import * as LandingPagesApi from '@/api/landing-pages'

const state = {
  list: [],
  current: null,        // the page currently viewed/edited
  loading: false,
  error: null,
  pollTimerId: null,    // interval timer for status polling
}

const getters = {
  landingPages: (state) => state.list,
  landingPage:  (state) => state.current,
  landingPageStatus: (state) => state.current?.status || null,
}

const actions = {
  // LIST
  async getPages({ commit }, params) {
    commit(mutationTypes.FETCH_LANDING_PAGES)
    try {
      const res = await LandingPagesApi.getPages(params)
      commit(mutationTypes.FETCH_LANDING_PAGES_SUCCESS, res.data || [])
      return res
    } catch (err) {
      commit(mutationTypes.FETCH_LANDING_PAGES_FAILURE, err)
      throw err
    }
  },

  // SHOW
  async getPage({ commit }, { refId }) {
    commit(mutationTypes.FETCH_LANDING_PAGE)
    try {
      const res = await LandingPagesApi.getPage(refId)
      commit(mutationTypes.FETCH_LANDING_PAGE_SUCCESS, res.data)
      return res
    } catch (err) {
      commit(mutationTypes.FETCH_LANDING_PAGE_FAILURE, err)
      throw err
    }
  },

  // CREATE (kick off generation)  
  async createPage({ commit }, payload) {
    // payload can be FormData (for logo) or plain object
    commit(mutationTypes.CREATE_LANDING_PAGE)
    try {
      const res = await LandingPagesApi.createPage(payload)
      commit(mutationTypes.CREATE_LANDING_PAGE_SUCCESS, res.data)
      return res // should include { ref_id, status }
    } catch (err) {
      commit(mutationTypes.CREATE_LANDING_PAGE_FAILURE, err)
      throw err
    }
  },

  // POLLING: call this after createPage (or on refresh) until status is ready/failed
  startPollingStatus({ state, commit, dispatch }, { refId, intervalMs = 2000 }) {
    // clear any previous poller
    if (state.pollTimerId) {
      clearInterval(state.pollTimerId)
      commit(mutationTypes.CLEAR_LANDING_PAGE_POLL_TIMER)
    }
    const id = setInterval(async () => {
      try {
        const res = await LandingPagesApi.getPage(refId)
        const data = res.data
        commit(mutationTypes.FETCH_LANDING_PAGE_SUCCESS, data)
        commit(mutationTypes.SET_LANDING_PAGE_STATUS, data.status)
        if (data.status === 'ready' || data.status === 'failed') {
          clearInterval(id)
          commit(mutationTypes.CLEAR_LANDING_PAGE_POLL_TIMER)
        }
      } catch (e) {
        // stop polling on hard error
        clearInterval(id)
        commit(mutationTypes.CLEAR_LANDING_PAGE_POLL_TIMER)
      }
    }, intervalMs)
    commit(mutationTypes.SET_LANDING_PAGE_POLL_TIMER, id)
  },

  stopPollingStatus({ state, commit }) {
    if (state.pollTimerId) {
      clearInterval(state.pollTimerId)
      commit(mutationTypes.CLEAR_LANDING_PAGE_POLL_TIMER)
    }
  },

  // EDIT HTML (from in-app editor)
  async updatePageHtml({ commit }, { refId, html }) {
    const res = await LandingPagesApi.updatePageHtml(refId, html)
    commit(mutationTypes.UPDATE_LANDING_PAGE_HTML_SUCCESS, { refId })
    return res
  },

  // REGENERATE (single latest version kept server-side)
  async regeneratePage({ commit, dispatch }, { refId, overrides }) {
    const res = await LandingPagesApi.regeneratePage(refId, overrides)
    commit(mutationTypes.REGENERATE_LANDING_PAGE_SUCCESS, { refId })
    // immediately start polling again
    dispatch('startPollingStatus', { refId })
    return res
  },

  // DELETE (Single)
  async deletePage({ commit }, { refId }) {
    commit(mutationTypes.DELETE_LANDING_PAGE)
    const res = await LandingPagesApi.deletePage(refId)
    commit(mutationTypes.DELETE_LANDING_PAGE_SUCCESS, { refId })
    return res
  },

  // DELETE (Bulk) - efficient bulk deletion using backend endpoint
  async deletePages({ commit }, { refIds }) {
    commit(mutationTypes.DELETE_LANDING_PAGES)
    
    try {
      const res = await LandingPagesApi.bulkDeletePages(refIds)
      
      // Handle successful deletions by updating the store
      if (res.data && res.data.results && res.data.results.deleted) {
        res.data.results.deleted.forEach(item => {
          commit(mutationTypes.DELETE_LANDING_PAGE_SUCCESS, { refId: item.ref_id })
        })
      }
      
      commit(mutationTypes.DELETE_LANDING_PAGES_SUCCESS)
      return res
    } catch (error) {
      commit(mutationTypes.DELETE_LANDING_PAGES_FAILURE, error)
      throw error
    }
  },
}

const mutations = {
  [mutationTypes.FETCH_LANDING_PAGES](state) {
    state.loading = true
    state.error = null
  },
  [mutationTypes.FETCH_LANDING_PAGES_SUCCESS](state, data) {
    state.loading = false
    // Handle nested Laravel response structure: data.data or data
    const listData = data?.data || data
    state.list = Array.isArray(listData) ? listData : []
  },
  [mutationTypes.FETCH_LANDING_PAGES_FAILURE](state, err) {
    state.loading = false
    state.error = err
  },

  [mutationTypes.FETCH_LANDING_PAGE](state) {
    state.loading = true
    state.error = null
  },
  [mutationTypes.FETCH_LANDING_PAGE_SUCCESS](state, data) {
    state.loading = false
    state.current = data || null
  },
  [mutationTypes.FETCH_LANDING_PAGE_FAILURE](state, err) {
    state.loading = false
    state.error = err
  },

  [mutationTypes.CREATE_LANDING_PAGE](state) {
    state.error = null
  },
  [mutationTypes.CREATE_LANDING_PAGE_SUCCESS](state, data) {
    // Optionally pre-populate current with { ref_id, status }
    state.current = { ...(state.current || {}), ...data }
  },
  [mutationTypes.CREATE_LANDING_PAGE_FAILURE](state, err) {
    state.error = err
  },

  [mutationTypes.UPDATE_LANDING_PAGE_HTML_SUCCESS](state) {
    // no-op; your UI can refetch show if needed
  },

  [mutationTypes.REGENERATE_LANDING_PAGE_SUCCESS](state) {
    // no-op; polling will refresh status
  },

  [mutationTypes.DELETE_LANDING_PAGE](state) {},
  [mutationTypes.DELETE_LANDING_PAGE_SUCCESS](state, { refId }) {
    // Ensure state.list is always an array before operating on it
    if (!Array.isArray(state.list)) {
      state.list = []
      return
    }
    
    const idx = state.list.findIndex(p => p.id === refId || p.ref_id === refId)
    if (idx > -1) {
      state.list.splice(idx, 1)
    }
    
    // Clear current if it matches the deleted item
    if (state.current && (state.current.id === refId || state.current.ref_id === refId)) {
      state.current = null
    }
  },

  // Bulk delete mutations
  [mutationTypes.DELETE_LANDING_PAGES](state) {
    state.loading = true
    state.error = null
  },
  [mutationTypes.DELETE_LANDING_PAGES_SUCCESS](state) {
    state.loading = false
    // Individual items are removed by DELETE_LANDING_PAGE_SUCCESS mutations
  },
  [mutationTypes.DELETE_LANDING_PAGES_FAILURE](state, errors) {
    state.loading = false
    state.error = errors
  },

  [mutationTypes.SET_LANDING_PAGE_STATUS](state, status) {
    if (state.current) state.current.status = status
  },

  [mutationTypes.SET_LANDING_PAGE_POLL_TIMER](state, id) {
    state.pollTimerId = id
  },
  [mutationTypes.CLEAR_LANDING_PAGE_POLL_TIMER](state) {
    state.pollTimerId = null
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
